import {
  AfterViewInit,
  Component,
  computed,
  effect,
  OnDestroy,
  OnInit,
  QueryList,
  Signal,
  signal,
  TemplateRef,
  ViewChild,
  ViewChildren,
  WritableSignal,
  inject,
} from '@angular/core';
import { TableColumn } from '@swimlane/ngx-datatable';
import { HttpParams } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { OffcanvasService } from '../../../../_services/offcanvas-service/offcanvas.service';
import { ActivatedRoute, Params, Router, RouterLink, RouterOutlet } from '@angular/router';
import { UpdatePageTitle } from '../../../../store/app/app.actions';
import { hasAccountingTransactionTableColumns, noAccountingTransactionTableColumns } from './_utils/transactions-utils';
import { TransactionSearchOptions } from '../../../../shared/components/forms/search-field/search-utils';
import { AppTableComponent } from '../../../../shared/components/table/app-table.component';
import { CurrencyPipe, DatePipe, NgClass } from '@angular/common';
import { EMPTY, Observable, Subscription } from 'rxjs';
import { PaginatedResults } from '@bo/ng-api';
import { TransactionsApiService } from '../../../../_services/transactions-service/transactions-api.service';
import { Transaction, TransactionStatusLabel, TransactionType } from '../../../../_interfaces/transactions.interface';
import { updateParamsObject } from '../../../../shared/utils/filter-utils';
import { DatatableComponent } from '@bo/ng-datatable';
import { AccountingApiService } from '../../../../_services/accounting-service/accounting-api/accounting-api.service';
import {
  AccountingAccount,
  AccountingConnectionStatus,
  AccountingProviderCompany,
  AccountingSyncStatus,
  AccountingSyncType,
} from '../../../../_interfaces/accounting.interface';
import {
  ClearTransactionsSyncStatus,
  SetTransactionSyncStatus,
  StartPollingTransactionSyncStatus,
} from '../../../../store/transactions/transactions.actions';
import { TransactionsSelectors } from '../../../../store/transactions/transactions.selectors';
import { ToastService } from '../../../../_services/toast-service/toast.service';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TransactionFilterComponent } from './transaction-filter/transaction-filter.component';
import { DashboardApiService } from '../../../../_services/dashboard-api-service/dashboard-api.service';
import {
  StatusBadgeComponent,
  statusBadgeDepartments,
} from '../../../../shared/components/status-badge/status-badge.component';
import { TableHeaderTemplateComponent } from '../../../../shared/components/table-header-template/table-header-template.component';
import { AccountingService } from '../_services/accounting/accounting.service';
import { switchMap } from 'rxjs/operators';
import { SetAccountingProviderStateFromTransactionSyncFailure } from '../../../../store/accounting/accounting.actions';
import { AccountingModalService } from '../_services/modals/accounting-modal.service';
import { DepositsComponent } from './deposits/deposits.component';
import { ToggleLabelSwitchComponent } from '../../../../shared/components/forms/toggle-label-switch/toggle-label-switch.component';
import { TransactionsState } from '../../../../store/transactions/transactions.state';
import { SubscriptionSelectors } from '../../../../store/subscription/subscription.selectors';
import { toSignal } from '@angular/core/rxjs-interop';
import { EmptyIndicatorPipe } from '../../../../shared/pipes/empty-indicator.pipe';
import { AccountingSelectors } from '../../../../store/accounting/accounting.selectors';
import { returnVatTypeLabel } from './_helpers/transactions-helpers';

@Component({
  selector: 'app-transactions',
  imports: [
    AppTableComponent,
    DatePipe,
    RouterOutlet,
    CurrencyPipe,
    NgClass,
    RouterLink,
    TooltipModule,
    NgbTooltip,
    TransactionFilterComponent,
    StatusBadgeComponent,
    TableHeaderTemplateComponent,
    DepositsComponent,
    ToggleLabelSwitchComponent,
    EmptyIndicatorPipe,
  ],
  templateUrl: './transactions.component.html',
  styleUrls: ['../../../../../static/styles/components/transactions.scss'],
  standalone: true,
})
export class TransactionsComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly store = inject(Store);
  readonly offcanvasService = inject(OffcanvasService);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly transactionsApiService = inject(TransactionsApiService);
  readonly transactionsState = inject(TransactionsState);
  readonly accountingApiService = inject(AccountingApiService);
  readonly accountingService = inject(AccountingService);
  readonly accountingModalService = inject(AccountingModalService);
  readonly dashboardApiService = inject(DashboardApiService);
  readonly toastService = inject(ToastService);

  readonly syncStatus$ = this.store.select(TransactionsSelectors.transactionSyncStatus);
  readonly catSyncStatus$ = this.store.select(TransactionsSelectors.categoriesSyncStatus);
  readonly busySyncing$ = this.store.select(TransactionsSelectors.busySyncing);
  readonly hasAccounting$ = this.store.select(SubscriptionSelectors.hasAccounting);
  readonly syncEnabled$ = this.store.select(AccountingSelectors.syncEnabled);

  protected readonly catSyncStatus = toSignal(this.catSyncStatus$, { initialValue: undefined });
  protected readonly hasAccounting = toSignal(this.hasAccounting$, { initialValue: false });
  protected readonly syncEnabled = toSignal(this.syncEnabled$, { initialValue: false });

  protected hasActiveConnection: Signal<boolean> = computed(() => {
    return this.accountingService.connectionStatus() === AccountingConnectionStatus.Complete;
  });

  protected noTenantSelection: Signal<boolean> = computed(() => {
    return this.accountingService.connectionStatus() === AccountingConnectionStatus.HasTenants;
  });
  protected readonly columnsInitialized = signal(false);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // TODO: TemplateRef<any> can be fixed by refactoring the @bo/ng-datatable library.
  @ViewChild('dateCell', { static: false }) dateCell!: TemplateRef<any>;
  @ViewChild('contactCell', { static: false }) contactCell!: TemplateRef<any>;
  @ViewChild('merchantCell', { static: false }) merchantCell!: TemplateRef<any>;
  @ViewChild('categoryCell', { static: false }) categoryCell!: TemplateRef<any>;
  @ViewChild('amountCell', { static: false }) amountCell!: TemplateRef<any>;
  @ViewChild('receiptCell', { static: false }) receiptCell!: TemplateRef<any>;
  @ViewChild('statusCell', { static: false }) statusCell!: TemplateRef<any>;
  @ViewChild('actionsCell', { static: false }) actionsCell!: TemplateRef<any>;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  @ViewChildren(ToggleLabelSwitchComponent) toggles!: QueryList<ToggleLabelSwitchComponent>;
  @ViewChild('table', { static: false }) table!: AppTableComponent<DatatableComponent>;
  @ViewChild('rowDetailTpl', { static: true }) rowDetailTpl?: TemplateRef<{ row: unknown }>;

  protected readonly TransactionSearchOptions = TransactionSearchOptions;
  protected columns: Array<TableColumn> = [];
  queryParams = new HttpParams();
  params: Params | undefined;
  readonly subscriptions = new Subscription();
  protected readonly statusBadgeDepartments = statusBadgeDepartments;
  protected loadingActiveConnection: WritableSignal<boolean> = signal(true);
  protected hasTransactions: WritableSignal<boolean> = signal(false);
  protected busySyncing: WritableSignal<boolean> = signal(false);
  protected unsyncedTransactions: WritableSignal<number> = signal(0);
  protected readyTransactions: WritableSignal<number> = signal(0);
  protected hasUnsyncedLoading: WritableSignal<boolean> = signal(true);
  protected syncStatus: WritableSignal<AccountingSyncStatus | undefined> = signal(undefined);
  protected showDeposits: WritableSignal<boolean> = signal(false);
  // Exposed (not private) so it can be bound to the table's [suppressFetch] input
  // in the template, preventing the tables from fetching with stale/wrong-tab
  // params while a tab switch navigation is in flight. See AppTableComponent.
  protected isNavigating: boolean = false;

  protected filterParams: Signal<{ type: TransactionType }> = computed(() => {
    const queryParams = this.route.snapshot.queryParams;
    return {
      type: queryParams['type'] ?? '',
    };
  });

  readonly provider = computed(() => this.accountingService.provider());
  readonly connectionStatus = computed(() => this.accountingService.connectionStatus());

  protected connectToSync: Signal<boolean> = computed(
    () => !this.hasActiveConnection() || !this.hasAccounting() || !this.sourceAccount(),
  );

  protected readonly showSyncButton = computed(
    () =>
      this.hasActiveConnection() &&
      this.hasAccounting() &&
      this.sourceAccount() &&
      this.catSyncStatus() !== AccountingSyncStatus.Busy,
  );

  protected sourceAccount: Signal<AccountingAccount | undefined> = computed(() =>
    this.accountingService.sourceAccount(),
  );
  constructor() {
    this.store.dispatch(new UpdatePageTitle('Transactions'));
    this.offcanvasService.closeAfterNavigationEnd();

    effect(() => {
      const connectionStatus = this.connectionStatus();
      if (connectionStatus === AccountingConnectionStatus.AuthError) {
        this.showConnectionErrorModal();
      }
    });

    // Reactively update columns when accounting status changes
    effect(() => {
      const hasAcct = this.hasAccounting();
      const hasConnection = this.hasActiveConnection();
      const newColumns =
        hasAcct && hasConnection ? hasAccountingTransactionTableColumns : noAccountingTransactionTableColumns;

      // Only update if columns actually changed
      const currentColumnsLength = this.columns.filter((col) => col.prop !== '$$expandColumn').length;
      if (currentColumnsLength !== newColumns.length) {
        // Defer column update to avoid ExpressionChangedAfterItHasBeenCheckedError
        queueMicrotask(() => {
          // Preserve expand column if it exists
          const expandColumn = this.columns.find((col) => col.prop === '$$expandColumn');
          this.columns = expandColumn ? [expandColumn, ...newColumns] : [...newColumns];

          // Re-apply templates if they were already initialized
          if (this.columnsInitialized()) {
            this.applyTemplates();
            this.columns = [...this.columns];
          } else {
            // Templates haven't been initialized yet, try to set them up now
            this.setupColumns();
          }
        });
      }
    });
  }

  canExpandDocument = (row: unknown): boolean => {
    // Only show expand arrow for rows that have items (split transactions)
    const transaction = row as Transaction;
    return transaction.items !== undefined && transaction.items.length > 0;
  };

  // Helper to cast row to Transaction in template
  asTransaction = (row: unknown): Transaction => row as Transaction;

  ngOnInit() {
    // Load tracking categories and tax rates (with caching) when entering transactions page
    this.accountingService.loadTrackingCategories();
    this.accountingService.loadTaxRates();

    // Subscribe to route query params to sync view state with URL
    this.subscriptions.add(
      this.route.queryParams.subscribe({
        next: (params) => {
          const currentType = params['type'];
          const wasShowingDeposits = this.showDeposits();
          const nowShowingDeposits = currentType == TransactionType.DEPOSIT;

          // Update view state to match URL
          this.showDeposits.set(nowShowingDeposits);

          // If switching from Deposits back to Payments, reset columns to reinitialise expand functionality
          if (wasShowingDeposits && !nowShowingDeposits) {
            this.columnsInitialized.set(false);
            this.columns = [];
            this.resetPaymentColumns();
          }
        },
      }),
    );

    this.subscriptions.add(
      this.busySyncing$.subscribe({
        next: (res) => {
          this.busySyncing.set(res);
        },
      }),
    );

    this.getUnsyncedTransactionCount();

    this.subscriptions.add(
      this.syncStatus$.subscribe({
        next: (data) => {
          if (data) this.syncStatus.set(data);
          if (this.syncStatus() === AccountingSyncStatus.Busy && !this.busySyncing()) {
            this.store.dispatch(new StartPollingTransactionSyncStatus());
          }
          if (this.syncStatus() === AccountingSyncStatus.Done) {
            this.getUnsyncedTransactionCount();
            this.table?.reloadData();
          }
          if (this.syncStatus() === AccountingSyncStatus.Fail) {
            this.table?.reloadData();
            this.store.dispatch(new SetAccountingProviderStateFromTransactionSyncFailure());
          }
        },
      }),
    );

    this.subscriptions.add(
      this.transactionsState.stopPolling$.subscribe({
        next: () => {
          this.busySyncing.set(false);
          this.readyTransactions.set(0);
        },
      }),
    );
  }

  setViewPayments() {
    if (this.isNavigating) {
      return;
    }

    const currentType = this.route.snapshot.queryParams['type'];

    // Only navigate if currently showing deposits
    if (currentType == TransactionType.DEPOSIT) {
      this.isNavigating = true;

      // Switch the view synchronously, before navigating, so the deposits table
      // is torn down before the new query params arrive and can't react to them.
      this.showDeposits.set(false);
      this.columnsInitialized.set(false);
      this.columns = [];
      // Repopulate columns for the recreated app-table; nothing else does this
      // automatically once `columns` is cleared above.
      this.resetPaymentColumns();

      const existingParams = this.route.snapshot.queryParams;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { type, ...paramsWithoutType } = existingParams;

      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: { ...paramsWithoutType, page: '1' },
        })
        .then(() => {
          this.isNavigating = false;
        });
    }
  }

  setViewDeposits() {
    if (this.isNavigating) {
      return;
    }

    const currentType = this.route.snapshot.queryParams['type'];

    // Only navigate if NOT currently showing deposits
    if (currentType != TransactionType.DEPOSIT) {
      this.isNavigating = true;

      // Switch view synchronously before navigating, same reason as setViewPayments().
      this.showDeposits.set(true);

      const existingParams = this.route.snapshot.queryParams;

      // Keep only specific properties supported on deposit view
      const newParams = {
        page: existingParams['page'],
        status: existingParams['status'],
        date_from: existingParams['date_from'],
        date_to: existingParams['date_to'],
      };

      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: { ...newParams, type: TransactionType.DEPOSIT, page: '1' },
        })
        .then(() => {
          this.isNavigating = false;
        });
    }
  }

  getUnsyncedTransactionCount() {
    this.subscriptions.add(
      this.dashboardApiService.getAccountingWidgetDetails().subscribe({
        next: (data) => {
          this.hasUnsyncedLoading.set(false);
          this.loadingActiveConnection.set(false);
          this.unsyncedTransactions.set(data.unsynced_transactions);
          if (this.hasActiveConnection()) {
            this.store.dispatch(new SetTransactionSyncStatus());
          }
        },
      }),
    );
  }

  ngAfterViewInit() {
    this.setupColumns();
  }

  /**
   * Called when table data is loaded
   * @param dataLength - The number of records in the table
   */
  onDataLoaded(dataLength: number): void {
    this.hasTransactions.set(dataLength > 0);
  }

  private setupColumns(): void {
    // Guard: every column that expects a template has to have one available
    const templates = this.templateMap;
    const templatesReady = this.columns.every((col) => !col.prop || !(col.prop in templates) || !!templates[col.prop]);
    if (!templatesReady) {
      return;
    }

    // Apply templates to columns
    this.applyTemplates();

    // Reassign array to trigger change detection in the table
    this.columns = [...this.columns];
    this.columnsInitialized.set(true);
  }

  /**
   * Rebuild the Payments table's columns and re-apply cell templates. Needed
   * after `columns` is cleared when switching back from Deposits to Payments.
   */
  private resetPaymentColumns(): void {
    const newColumns =
      this.hasAccounting() && this.hasActiveConnection()
        ? hasAccountingTransactionTableColumns
        : noAccountingTransactionTableColumns;
    this.columns = [...newColumns];
    this.setupColumns();
  }

  /**
   * Maps column props to their cell templates. A template may be undefined if it
   * hasn't rendered yet or isn't part of the current view.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private get templateMap(): Record<string, TemplateRef<any> | undefined> {
    return {
      'timestamp': this.dateCell,
      'contact': this.contactCell,
      'merchant': this.merchantCell,
      'category': this.categoryCell,
      'amount': this.amountCell,
      'receipt_status': this.receiptCell,
      'status': this.statusCell,
      'actions': this.actionsCell,
    };
  }

  private applyTemplates(): void {
    const templates = this.templateMap;
    for (const column of this.columns) {
      if (column.prop === '$$expandColumn' || !column.prop) continue;
      const template = templates[column.prop];
      if (template) {
        column.cellTemplate = template;
      }
    }
  }

  onViewTransaction(id: string) {
    this.offcanvasService.offCanvasNavigate(`/accounting/transactions/edit/${id}`);
  }

  getData(queryParams: HttpParams): Observable<PaginatedResults<Transaction>> {
    return this.transactionsApiService.getTransactions(queryParams);
  }

  update(params: HttpParams) {
    this.params = updateParamsObject(params);
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: updateParamsObject(params),
      })
      .then(() => {});
  }

  showCategoryAlert(row: Transaction) {
    return (
      row.category === null ||
      (this.hasAccounting() && this.hasActiveConnection() && !row.category?.account) ||
      !row.category?.is_visible
    );
  }

  hasCatAccount(row: Transaction): boolean {
    return Boolean(row.category?.account);
  }

  statusNew(row: Transaction): boolean {
    return row.status === TransactionStatusLabel.NEW;
  }

  statusReady(row: Transaction): boolean {
    return row.status === TransactionStatusLabel.READY;
  }

  statusError(row: Transaction): boolean {
    return row.status === TransactionStatusLabel.ERROR;
  }

  statusSynched(row: Transaction): boolean {
    return row.status === TransactionStatusLabel.SYNCHED;
  }

  /**
   * Find a toggle component by its ID
   */
  private findToggleById(transactionId: string): ToggleLabelSwitchComponent | undefined {
    return this.toggles?.find((toggle) => toggle.id() === `toggle_${transactionId}`);
  }

  markReadyButtonDisabled(row: Transaction) {
    const isMainCategory = Boolean(!row.items?.length);
    const hasAccountingAndIsConnected = this.hasAccounting() && this.hasActiveConnection();
    return Boolean(
      // If no active connection and transaction is not ready
      (!this.hasActiveConnection() && !this.statusReady(row)) ||
      // If category is null or (has accounting and active connection and category has no account) and no items
      (Boolean(row.category === null || (hasAccountingAndIsConnected && !row.category?.account)) && isMainCategory) ||
      // If contact has no name and transaction is not ready
      Boolean(!row.contact?.name && !this.statusReady(row)) ||
      // If transaction is flagged
      row.flag ||
      // If is main category, is active connection has no tax id  has accounting
      (hasAccountingAndIsConnected && isMainCategory && !row.tax?.id && !this.statusReady(row)),
    );
  }

  showContactErrorOnReady(row: Transaction) {
    return Boolean(!row.contact?.name && this.statusReady(row));
  }

  showTaxIdErrorOnReady(row: Transaction) {
    return Boolean(!row.tax?.id && this.statusReady(row) && !row.items?.length);
  }

  markTransactionReady(transactionId: string) {
    this.subscriptions.add(
      this.transactionsApiService.markTransactionReady(transactionId).subscribe({
        next: () => {
          this.updateTransactionStatus(transactionId, TransactionStatusLabel.READY);
        },
        error: (error) => {
          // Revert the toggle to false (NEW state) using updateValue model
          const toggle = this.findToggleById(transactionId);
          if (toggle) {
            toggle.updateValue.set(false);
          }
          this.toastService.show(
            `Could not mark transaction as ready: ${error.error?.non_field_errors}`,
            'iconoir-xmark',
          );
        },
      }),
    );
  }

  unmarkTransactionReady(transactionId: string) {
    this.subscriptions.add(
      this.transactionsApiService.unmarkTransactionReady(transactionId).subscribe({
        next: () => {
          this.updateTransactionStatus(transactionId, TransactionStatusLabel.NEW);
        },
        error: (error) => {
          // Revert the toggle to true (READY state) using updateValue model
          const toggle = this.findToggleById(transactionId);
          if (toggle) {
            toggle.updateValue.set(true);
          }
          this.toastService.show(`An error occurred: ${error.error?.non_field_errors}`, 'iconoir-xmark');
        },
      }),
    );
  }

  /**
   * Update a transaction's status in the local table data without reloading
   */
  private updateTransactionStatus(transactionId: string, newStatus: TransactionStatusLabel): void {
    if (!this.table?.data) return;

    // Find and update the transaction in the current page's data
    const transactions = this.table.data as Transaction[];
    const transaction = transactions.find((t) => t.id === transactionId);

    if (transaction) {
      // Update the status
      transaction.status = newStatus;

      // Trigger change detection by creating a new array reference
      this.table.data = [...transactions];
    }
  }

  syncTransactions() {
    this.subscriptions.add(
      this.transactionsApiService
        .getReadyTransactions()
        .pipe(
          switchMap((data) => {
            this.readyTransactions.set(data.length);
            if (this.readyTransactions() > 0) {
              // Only sync if there are ready transactions
              this.busySyncing.set(true);

              // Clear and start polling BEFORE making the API call
              // This ensures polling begins immediately, not after the PUT request completes
              this.store.dispatch(new ClearTransactionsSyncStatus());
              this.store.dispatch(new StartPollingTransactionSyncStatus());

              return this.accountingApiService.requestAccountingSync(AccountingSyncType.Transactions);
            } else {
              // No ready transactions - return empty observable to complete the chain
              this.busySyncing.set(false);
              this.toastService.show('No transactions selected ready to sync.', 'iconoir-info-circle');
              return EMPTY;
            }
          }),
        )
        .subscribe({
          next: () => {
            // Sync request initiated successfully - polling will track the status
          },
          error: (error) => {
            this.toastService.show(
              `Something went wrong. ${error.error?.non_field_errors ? error.error?.non_field_errors : ''}`,
              'iconoir-warning-triangle',
            );
            this.store.dispatch(new SetAccountingProviderStateFromTransactionSyncFailure());
            this.busySyncing.set(false);
          },
        }),
    );
  }

  onExport() {
    const hasFiltersApplied = Object.keys(this.route.snapshot.queryParams).length > 2;
    if (!this.showDeposits() && hasFiltersApplied) {
      this.offcanvasService.offCanvasNavigate(`/accounting/transactions/export`);
    } else {
      this.accountingModalService.exportTransactionsModal(
        this.showDeposits() ? TransactionType.DEPOSIT : TransactionType.PAY,
      );
    }
  }

  showConnectionErrorModal() {
    this.accountingModalService.showConnectionErrorModal();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected readonly Array = Array;
  protected readonly AccountingProviderCompany = AccountingProviderCompany;
  protected readonly Boolean = Boolean;
  protected readonly returnVatTypeLabel = returnVatTypeLabel;
  protected readonly AccountingSyncStatus = AccountingSyncStatus;
}
